import * as Sentry from '@sentry/browser';
import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { io } from 'socket.io-client';
import { debounce } from 'throttle-debounce';

import {
  aiReportsLocationQueryKey,
  aiReportsLocationsQueryKey,
} from './pages/aiReports/data/aiReportsDataProvider';
import { features } from './pages/dashboards/dashboardFramework/helpers/features';
import {
  downloadRentNavigatorReport,
  reamsPropertiesQueryKey,
  reamsPropertyQueryKey,
  rentNavigatorNewReport,
} from './pages/rentNavigator/data/rentNavigatorDataProvider';

const SOCKETIO_SERVER_URL = process.env.REACT_APP_SOCKETIO_SERVER_URL;

const REAMS_SHAREPOINT_LOCATIONS_RELOAD_EVENT =
  'reamsSharepointLocationsReloadEvent';

const REAMS_LOCATION_REPORT_RELOAD_EVENT = 'reamsLocationReportReloadEvent';
const AI_LOCATION_REPORT_RELOAD_EVENT = 'aiLocationReportReloadEvent';

// 10 minutes until the socket disconnects if the tab is not in focus
const DISCONNECT_TIMEOUT_IN_MS = 10 * 60 * 1000;

const SocketIoWrapper = ({
  children,
  organizationId,
  globalFeatures,
  globalTrialFeatures,
}) => {
  const queryClient = useQueryClient();

  const socket = useRef();
  const closeConnectionTimeoutIdRef = useRef();
  const isTabVisible = useRef(document.hasFocus());

  const location = useLocation();

  const isOverviewPath = location.pathname.startsWith(
    '/optimize/rent-navigator/overview',
  );

  const isOverviewPathRef = useRef(isOverviewPath);

  useEffect(() => {
    isOverviewPathRef.current = isOverviewPath;
  }, [isOverviewPath]);

  const closeSocket = useCallback(() => {
    socket.current?.disconnect();
    socket.current = null;
  }, []);

  const logSentryError = useMemo(
    () =>
      debounce(
        60 * 1000,
        () => {
          Sentry.withScope((scope) => {
            scope.setTag('component', 'SocketIoWrapper');
            scope.setLevel('error');
            Sentry.captureMessage(
              'Error connecting to socket io server: socket-initializatio',
            );
          });
        },
        { atBegin: true },
      ),
    [],
  );

  const hasNoFeaturesSupportSocket = useMemo(() => {
    const socketSupportFeatures = [features.REAMS, features.AI_REPORTS];
    const combinedLicenses = [...globalFeatures, ...globalTrialFeatures];

    // No supported features → do not initialize sockets
    return socketSupportFeatures.every(
      (feature) => !combinedLicenses.includes(feature),
    );
  }, [globalFeatures, globalTrialFeatures]);

  const handleSavedLocationReport = useCallback(
    (locationId, dataset) => {
      queryClient.invalidateQueries({
        queryKey: [reamsPropertyQueryKey, locationId, dataset],
        exact: false,
      });
      queryClient.setQueriesData(
        { queryKey: [reamsPropertiesQueryKey] },
        (oldData) => {
          if (!oldData?.properties) return oldData;

          const index = oldData.properties.findIndex(
            (p) => p.id === locationId && p.dataset === dataset,
          );

          if (index === -1) return oldData;

          const properties = [...oldData.properties];
          properties[index] = { ...properties[index], reportStatus: 'done' };

          return { ...oldData, properties };
        },
      );
    },
    [queryClient],
  );

  const handleUnsavedLocationReport = useCallback(
    (locationId, dataset, isNewReport) => {
      queryClient.setQueryData(
        [reamsPropertyQueryKey, locationId, dataset],
        (oldProperty) => {
          if (!oldProperty) return oldProperty;

          downloadRentNavigatorReport({
            propertyId: locationId,
            locationName: 'chaos-rent-navigator-report.pptx',
            dataset: oldProperty.dataset,
          }).then();

          if (!isNewReport) return oldProperty;

          queryClient.setQueryData([rentNavigatorNewReport, locationId], false);

          return {
            ...oldProperty,
            reportStatus: 'done',
          };
        },
      );
    },
    [queryClient],
  );

  const initializeSocketConnection = useCallback(() => {
    if (!organizationId || hasNoFeaturesSupportSocket || socket.current) {
      return undefined;
    }
    socket.current = io(SOCKETIO_SERVER_URL, {
      auth: { token: localStorage.getItem('token') },
    });

    socket.current.on(REAMS_SHAREPOINT_LOCATIONS_RELOAD_EVENT, () => {
      queryClient.invalidateQueries({
        queryKey: [reamsPropertyQueryKey],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: [reamsPropertiesQueryKey],
        exact: false,
      });
    });

    socket.current.on(REAMS_LOCATION_REPORT_RELOAD_EVENT, (data) => {
      const { locationId, dataset } = data;
      if (locationId && dataset) {
        if (isOverviewPathRef.current) {
          const isNewReport =
            queryClient.getQueryData([rentNavigatorNewReport, locationId]) ||
            false;
          handleUnsavedLocationReport(locationId, dataset, isNewReport);
        } else {
          handleSavedLocationReport(locationId, dataset);
        }
      }
    });

    socket.current.on(AI_LOCATION_REPORT_RELOAD_EVENT, (data) => {
      const { locationId } = data;
      if (locationId) {
        queryClient.invalidateQueries({
          queryKey: [aiReportsLocationQueryKey, locationId],
          exact: false,
        });
        queryClient.setQueriesData(
          { queryKey: [aiReportsLocationsQueryKey] },
          (oldData) => {
            if (!oldData?.properties) return oldData;

            const index = oldData.properties.findIndex(
              (p) => p.id === locationId,
            );

            if (index === -1) return oldData;

            const properties = [...oldData.properties];
            properties[index] = { ...properties[index], reportStatus: 'done' };

            return { ...oldData, properties };
          },
        );
      }
    });

    socket.current.on('error', (e) => {
      if (e === 'socket-initialization') {
        logSentryError();
        closeSocket();

        setTimeout(() => {
          if (isTabVisible.current) {
            initializeSocketConnection();
          }
        }, 5000);
      }
    });

    return () => {
      closeSocket();
    };
  }, [
    closeSocket,
    handleSavedLocationReport,
    handleUnsavedLocationReport,
    hasNoFeaturesSupportSocket,
    logSentryError,
    organizationId,
    queryClient,
  ]);

  useEffect(() => {
    if (isTabVisible.current) {
      initializeSocketConnection();
    }
  }, [initializeSocketConnection]);

  const onWindowFocus = useCallback(() => {
    isTabVisible.current = true;
    clearTimeout(closeConnectionTimeoutIdRef.current);
    initializeSocketConnection();
  }, [initializeSocketConnection]);

  const onWindowBlur = useCallback(() => {
    closeConnectionTimeoutIdRef.current = setTimeout(() => {
      closeSocket();
    }, DISCONNECT_TIMEOUT_IN_MS);
    isTabVisible.current = false;
  }, [closeSocket]);

  useEffect(() => {
    window.addEventListener('focus', onWindowFocus);
    window.addEventListener('blur', onWindowBlur);

    return () => {
      window.removeEventListener('focus', onWindowFocus);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [onWindowBlur, onWindowFocus]);

  return children;
};

SocketIoWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  organizationId: PropTypes.string,
};

SocketIoWrapper.defaultProps = {
  organizationId: null,
};

function mapStateToProps(state) {
  return {
    organizationId: state?.user?.info?.companyId,
    globalFeatures: state?.user?.globalFeatures || [],
    globalTrialFeatures: state?.user?.globalTrialFeatures || [],
  };
}

export default connect(mapStateToProps)(SocketIoWrapper);
